if (!customElements.get('filtering-media-gallery')) {
  customElements.whenDefined('media-gallery').then((MediaGallery) => {
    customElements.define(
      'filtering-media-gallery',
      class FilteringMediaGallery extends MediaGallery {
        constructor() {
          super();

          this.onVariantChangeUnsubscriber = undefined;
          this.onQuickViewLoadedHandler = this.handleQuickViewLoaded.bind(this);
          this.handleVariantChangeBound = this.handleVariantChange.bind(this);
        }

        connectedCallback() {
          this.subscribeToVariantEvents();
          document.addEventListener('quick-view:loaded', this.onQuickViewLoadedHandler);
          this.preFilter();
          FoxTheme.Motion.inView(this, this.init.bind(this));
        }

        // Runs synchronously during custom element upgrade. Unlike before, this
        // no longer needs to race paint: sc-filtered-media-gallery.liquid
        // already rendered only the slides/thumbs matching the selected
        // variant into the swiper wrappers, and put everything else into
        // FilteredMediaStore/FilteredThumbStore with a native `hidden`
        // attribute (applied synchronously at HTML-parse time, no CSS or JS
        // dependency). So all preFilter() has to do on load is wire up the
        // lookup maps for *future* variant changes — the initial DOM is
        // already correct by construction.
        preFilter() {
          if (this.preFiltered) return;

          this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
          this.productOptions = this.parseProductOptions();
          this.filterOptionGroups = this.parseFilterOptionGroups();
          this.optionTagIndex = this.compileOptionTagIndex();
          this.mediaItemMap = this.buildMediaItemMap();
          this.variantVisibilityCache = this.buildVariantVisibilityCache();
          this.setupFilteredSlideStore();

          this.preFiltered = true;
        }

        disconnectedCallback() {
          this.unsubscribeFromVariantEvents();
          document.removeEventListener('quick-view:loaded', this.onQuickViewLoadedHandler);
          this.teardown();
        }

        subscribeToVariantEvents() {
          if (this.onVariantChangeUnsubscriber) {
            return;
          }

          this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this.handleVariantChangeBound,
          );
        }

        unsubscribeFromVariantEvents() {
          if (this.onVariantChangeUnsubscriber) {
            this.onVariantChangeUnsubscriber();
            this.onVariantChangeUnsubscriber = undefined;
          }
        }

        handleVariantChange(event) {
          const { sectionId, variant } = event.data || {};

          if (!variant || !sectionId || sectionId !== this.dataset.section) {
            return;
          }

          const productInfo = this.closest('product-info');

          if (!productInfo || productInfo.dataset.section !== sectionId) {
            return;
          }

          this.init();
          this.setActiveMedia(variant);
        }

        handleQuickViewLoaded() {
          const modal = this.closest('quick-view-modal');

          if (!modal || !this.isConnected) {
            return;
          }

          requestAnimationFrame(() => {
            this.init();
            this.syncWithSelectedVariant();
          });
        }

        getSelectedVariantFromDOM() {
          const productInfo = this.closest('product-info');

          if (!productInfo) {
            return null;
          }

          const selectedVariant = productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent;

          if (!selectedVariant) {
            return null;
          }

          try {
            return JSON.parse(selectedVariant);
          } catch (error) {
            console.error('Failed to parse selected variant for media gallery:', error);
            return null;
          }
        }

        syncWithSelectedVariant({ filter = true } = {}) {
          const variant = this.getSelectedVariantFromDOM();

          if (!variant) {
            return;
          }

          this.setActiveMedia(variant, { filter });
        }

        restoreAllSlidesToGallery() {
          if (!this.elements?.mediaList) {
            return;
          }

          const mediaList = this.elements.mediaList;
          const thumbList = this.getThumbList();

          if (this.filteredSlideStore) {
            [...this.filteredSlideStore.children].forEach((item) => {
              this.insertMediaItemInOrder(item, mediaList);
            });
          }

          if (thumbList && this.filteredThumbStore) {
            [...this.filteredThumbStore.children].forEach((thumb) => {
              this.insertThumbItemInOrder(thumb, thumbList);
            });
          }
        }

        teardown() {
          this.destroySlider();

          if (typeof this.thumbsInstance === 'object') {
            this.thumbsInstance.slider?.destroy();
            this.thumbsInstance = false;
          }

          this.restoreAllSlidesToGallery();
          this.initialized = false;
          this.preFiltered = false;
          this.elements = null;
        }

        init() {
          if (this.initialized === true) return;

          this.mediaLayout = this.dataset.mediaLayout;
          this.onlyImage = this.dataset.onlyImage === 'true';
          this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
          this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
          this.enableImageZoom = this.dataset.enableImageZoom === 'true';
          this.context = this.dataset.context;

          // Should always be true by now (connectedCallback runs preFilter()
          // before init()) — this is just a defensive fallback.
          if (!this.preFiltered) {
            this.preFilter();
          }

          this.setSliderOptions();

          // Liquid already rendered only the matching slides into the swiper
          // wrapper, in the same relative order as the matching thumbs, so
          // the featured slide's position among them is exactly the index
          // Swiper needs. Setting it on BOTH sliderOptions and thumbsOptions
          // (not just the main one) means the thumbs Swiper is born already
          // pointing at the right thumbnail — Swiper's Thumbs module has
          // nothing left to correct after the fact, which is what used to
          // cause the visible "jump" in the thumbnail strip on load.
          const initialSlide = this.getInitialSlideIndex();
          this.sliderOptions = { ...this.sliderOptions, initialSlide };
          this.thumbsOptions = { ...this.thumbsOptions, initialSlide };

          const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
          mql.onchange = this.updateMediaLayout.bind(this);
          this.updateMediaLayout();

          if (this.enableImageZoom) {
            this.initImageZoom();
          }

          this.initialized = true;
        }

        // Finds the slide Liquid marked with data-featured="true" (the
        // selected variant's featured media) among the already-visible
        // slides, so Swiper can be told its initialSlide directly instead of
        // starting at 0 and correcting afterwards.
        getInitialSlideIndex() {
          const mediaList = this.elements?.mediaList;

          if (!mediaList) {
            return 0;
          }

          const featuredEl = mediaList.querySelector('[data-featured="true"]');

          if (!featuredEl) {
            return 0;
          }

          const index = [...mediaList.children].indexOf(featuredEl);

          return index >= 0 ? index : 0;
        }

        initImageZoom() {
          const dataSource = this.buildLightboxDataSource();

          this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
            dataSource: dataSource,
            pswpModule: window.FoxTheme.PhotoSwipe,
            bgOpacity: 1,
            arrowPrev: false,
            arrowNext: false,
            zoom: false,
            close: false,
            counter: false,
            preloader: false,
          });

          this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
            const slider = this.sliderInstance?.slider;

            if (slider) {
              const { slides, activeIndex } = slider;
              if (slides[activeIndex]) {
                const el = slides[activeIndex].querySelector('img');
                if (el) {
                  return el;
                }
              }
            }

            return thumbEl;
          });

          this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
            const slider = this.sliderInstance?.slider;

            if (slider) {
              const { slides, activeIndex } = slider;
              if (slides[activeIndex]) {
                const el = slides[activeIndex].querySelector('img');
                if (el) {
                  return el.src;
                }
              }
            }

            return placeholderSrc;
          });

          this.lightbox.on('change', () => {
            window.pauseAllMedia(this);
          });

          let lastLightboxIndex = null;
          this.lightbox.on('close', () => {
            if (this.lightbox?.pswp?.currIndex) {
              lastLightboxIndex = this.lightbox.pswp.currIndex;
            }
          });

          this.lightbox.on('destroy', () => {
            const slider = this.sliderInstance?.slider;

            if (slider && lastLightboxIndex !== null) {
              const targetIndex = lastLightboxIndex;
              if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
                requestAnimationFrame(() => {
                  slider.slideTo(targetIndex, 0);
                });
              }
              lastLightboxIndex = null;
            }
          });

          this.lightbox.on('pointerDown', (e) => {
            if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
              e.preventDefault();
            }
          });

          this.lightbox.on('uiRegister', () => {
            const lightboxUI = this.lightbox?.pswp?.ui;

            if (!lightboxUI) return;

            if (!this.onlyImage) {
              lightboxUI.registerElement({
                name: 'next',
                ariaLabel: 'Next slide',
                order: 3,
                isButton: true,
                html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
                onClick: () => {
                  this.lightbox?.pswp?.next();
                },
              });
              lightboxUI.registerElement({
                name: 'prev',
                ariaLabel: 'Previous slide',
                order: 1,
                isButton: true,
                html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
                onClick: () => {
                  this.lightbox?.pswp?.prev();
                },
              });
            }
            lightboxUI.registerElement({
              name: 'close-zoom',
              ariaLabel: 'Close zoom image',
              order: 2,
              isButton: true,
              html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
              onClick: () => {
                this.lightbox?.pswp?.close();
              },
            });
          });

          this.lightbox.init();

          FoxTheme.utils.addEventDelegate({
            selector: '.js-photoswipe--zoom',
            context: this,
            handler: (e, media) => {
              if (media.dataset?.mediaType === 'image') {
                const slide = media.closest('.product__media-item');
                const visibleItems = this.getVisibleMediaItems();
                const index = visibleItems.indexOf(slide);

                if (index >= 0) {
                  this.lightbox.loadAndOpen(index);
                }
              }
            },
          });
        }

        // sc-filtered-media-gallery.liquid pre-renders these hidden stores
        // with the non-matching slides/thumbs already in them (see
        // FilteredMediaStore-/FilteredThumbStore- in that file). Falling back
        // to creating empty ones only guards against the stores somehow being
        // missing from the DOM.
        setupFilteredSlideStore() {
          if (this.filteredSlideStore) return;

          const sectionId = this.dataset.section;

          this.filteredSlideStore =
            document.getElementById(`FilteredMediaStore-${sectionId}`) || this.createHiddenStore();
          this.filteredThumbStore =
            document.getElementById(`FilteredThumbStore-${sectionId}`) || this.createHiddenStore();
        }

        createHiddenStore() {
          const store = document.createElement('div');
          store.className = 'visually-hidden';
          store.hidden = true;
          this.appendChild(store);
          return store;
        }

        getThumbList() {
          return this.elements.thumbnails?.querySelector('.swiper-wrapper') || null;
        }

        insertMediaItemInOrder(item, mediaList = this.elements.mediaList) {
          if (!mediaList) {
            return;
          }

          const mediaIndex = Number(item.dataset.mediaIndex);
          const insertBefore = [...mediaList.children].find(
            (sibling) => Number(sibling.dataset.mediaIndex) > mediaIndex,
          );

          if (insertBefore) {
            mediaList.insertBefore(item, insertBefore);
          } else {
            mediaList.appendChild(item);
          }
        }

        insertThumbItemInOrder(thumb, thumbList = this.getThumbList()) {
          if (!thumbList) {
            return;
          }

          const mediaId = thumb.dataset.target;
          const mainItem = this.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
          const mediaIndex = mainItem ? Number(mainItem.dataset.mediaIndex) : Number(thumb.dataset.mediaPosition);

          const insertBefore = [...thumbList.children].find((sibling) => {
            const siblingMediaId = sibling.dataset.target;
            const siblingMainItem = this.querySelector(`.product__media-item[data-media-id="${siblingMediaId}"]`);
            const siblingIndex = siblingMainItem
              ? Number(siblingMainItem.dataset.mediaIndex)
              : Number(sibling.dataset.mediaPosition);

            return siblingIndex > mediaIndex;
          });

          if (insertBefore) {
            thumbList.insertBefore(thumb, insertBefore);
          } else {
            thumbList.appendChild(thumb);
          }
        }

        isMediaItemInGallery(item, container = this.elements.mediaList) {
          return Boolean(item && container && item.parentElement === container);
        }

        parseFilterOptionGroups() {
          const raw = this.dataset.filterOptionGroups || '';

          return raw
            .split(',')
            .map((group) => this.slugify(group.trim()))
            .filter(Boolean);
        }

        isFilterableOptionKey(key) {
          return key !== 'hero' && this.filterOptionGroups.includes(key);
        }

        parseProductOptions() {
          try {
            return JSON.parse(this.dataset.productOptions || '[]');
          } catch (error) {
            console.error('Failed to parse product options for media gallery:', error);
            return [];
          }
        }

        slugify(value) {
          return value
            .toString()
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        }

        getMediaOptionTags(item) {
          const tags = {};
          const raw = item?.dataset?.mediaOptionTags || '';

          if (raw) {
            raw.split(',').forEach((pair) => {
              const separatorIndex = pair.indexOf(':');

              if (separatorIndex === -1) {
                return;
              }

              const key = this.slugify(pair.slice(0, separatorIndex).trim());
              const value = this.slugify(pair.slice(separatorIndex + 1).trim());

              if (key) {
                tags[key] = value;
              }
            });

            return tags;
          }

          const altText = item?.dataset?.mediaAlt || '';

          if (!altText) {
            return tags;
          }

          const optionTagRegex = /\[([^:\]]+):([^\]]+)\]/g;
          let match = optionTagRegex.exec(altText);

          while (match) {
            const key = this.slugify(match[1]);
            const value = this.slugify(match[2]);

            if (key && key !== 'hero') {
              tags[key] = value;
            }

            match = optionTagRegex.exec(altText);
          }

          return tags;
        }

        compileOptionTagIndex() {
          const index = {};
          const mediaItems = this.querySelectorAll('.product__media-item');

          mediaItems.forEach((item) => {
            const tags = this.getMediaOptionTags(item);

            Object.entries(tags).forEach(([key, value]) => {
              if (!this.isFilterableOptionKey(key)) {
                return;
              }

              if (!index[key]) {
                index[key] = [];
              }
              if (!index[key].includes(value)) {
                index[key].push(value);
              }
            });
          });

          Object.keys(index).forEach((key) => {
            index[key].sort();
          });

          return index;
        }

        getVariantOptionValues(variant) {
          const values = {};

          if (!variant || !this.productOptions.length) {
            return values;
          }

          this.productOptions.forEach(({ position, key }) => {
            if (!this.isFilterableOptionKey(key)) {
              return;
            }

            const optionValue = variant[`option${position}`];

            if (optionValue) {
              values[key] = this.slugify(optionValue);
            }
          });

          return values;
        }

        hasActiveOptionFiltering(variantValues) {
          return this.filterOptionGroups.some((key) => key in variantValues && this.optionTagIndex[key]?.length);
        }

        // Returns true if the item should be visible for the given pre-computed variantValues.
        shouldShowMediaItem(item, variantValues, isFilteringActive) {
          const imageTags = this.getMediaOptionTags(item);
          const tagKeys = Object.keys(imageTags).filter((key) => this.isFilterableOptionKey(key));

          return this.matchesFilterTags(imageTags, tagKeys, variantValues, isFilteringActive);
        }

        // Shared by shouldShowMediaItem (live path) and buildVariantVisibilityCache
        // (pre-computed path), which each obtain imageTags/tagKeys differently —
        // the former parses them fresh per call, the latter reuses a value
        // computed once per item outside the per-variant loop.
        matchesFilterTags(imageTags, tagKeys, variantValues, isFilteringActive) {
          if (tagKeys.length === 0 || !isFilteringActive) {
            return true;
          }

          return tagKeys.every((key) => {
            if (!(key in variantValues)) {
              return true;
            }

            return imageTags[key] === variantValues[key];
          });
        }

        buildMediaItemMap() {
          const map = new Map();

          this.querySelectorAll('.product__media-item').forEach((item) => {
            map.set(item.dataset.mediaId, item);
          });

          return map;
        }

        buildVariantVisibilityCache() {
          if (!this.filterOptionGroups.length || !Object.keys(this.optionTagIndex).length) {
            return null;
          }

          const cache = new Map();
          const allVariants = this.parseAllVariants();

          // Each item's option tags depend only on the item, never on the
          // variant, so they only need to be parsed once here — not once per
          // (variant, item) pair inside the loop below. With a full variant
          // list now (see parseAllVariants), that distinction is what keeps
          // this O(variants + media) instead of O(variants * media).
          const mediaTagEntries = [...this.querySelectorAll('.product__media-item')].map((item) => {
            const imageTags = this.getMediaOptionTags(item);
            const tagKeys = Object.keys(imageTags).filter((key) => this.isFilterableOptionKey(key));

            return { item, imageTags, tagKeys };
          });

          allVariants.forEach((variant) => {
            const variantValues = this.getVariantOptionValues(variant);
            const isFilteringActive = this.hasActiveOptionFiltering(variantValues);
            const visibleIds = new Set();

            mediaTagEntries.forEach(({ item, imageTags, tagKeys }) => {
              if (this.matchesFilterTags(imageTags, tagKeys, variantValues, isFilteringActive)) {
                visibleIds.add(item.dataset.mediaId);
              }
            });

            cache.set(String(variant.id), visibleIds);
          });

          return cache;
        }

        // Reads the real variant list from the MediaGalleryVariants-<sectionId>
        // script tag rendered by sc-filtered-media-gallery.liquid (only present
        // when filter option groups are actually configured — see
        // buildVariantVisibilityCache's early-return otherwise). Falls back to
        // the single selected variant if that tag is missing.
        parseAllVariants() {
          try {
            const sectionId = this.dataset.section;
            const jsonEl = document.getElementById(`MediaGalleryVariants-${sectionId}`);

            if (jsonEl) {
              const parsed = JSON.parse(jsonEl.textContent || '[]');
              return Array.isArray(parsed) ? parsed : [];
            }

            const el = this.closest('product-info')?.querySelector('[data-selected-variant]');
            if (!el) return [];

            const selected = JSON.parse(el.textContent || '[]');
            return selected ? [selected] : [];
          } catch {
            return [];
          }
        }

        getVisibleMediaItems() {
          if (!this.elements?.mediaList) {
            return [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          }

          return [...this.elements.mediaList.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        }

        filterMediaByVariant(variant, { refreshSlider = true } = {}) {
          if (!variant || !this.filterOptionGroups.length || !Object.keys(this.optionTagIndex).length) {
            return;
          }

          this.setupFilteredSlideStore();

          const mediaList = this.elements.mediaList;
          const thumbList = this.getThumbList();
          const mediaItems = this.querySelectorAll('.product__media-item');
          const thumbItems = this.querySelectorAll('.product__thumbs-item');

          // Use pre-computed cache when available, fall back to live evaluation.
          const visibleIds = this.variantVisibilityCache?.get(String(variant.id)) ?? this.computeVisibleIds(variant);

          mediaItems.forEach((item) => {
            const show = visibleIds.has(item.dataset.mediaId);
            const isInGallery = this.isMediaItemInGallery(item, mediaList);

            if (show && !isInGallery) {
              this.insertMediaItemInOrder(item, mediaList);
            } else if (!show && isInGallery) {
              this.filteredSlideStore.appendChild(item);
            }
          });

          if (thumbList) {
            thumbItems.forEach((thumb) => {
              const mainItem = this.mediaItemMap.get(thumb.dataset.target);
              const show = mainItem ? visibleIds.has(mainItem.dataset.mediaId) : false;
              const isInGallery = thumb.parentElement === thumbList;

              if (show && !isInGallery) {
                this.insertThumbItemInOrder(thumb, thumbList);
              } else if (!show && isInGallery) {
                this.filteredThumbStore.appendChild(thumb);
              }
            });
          }

          if (refreshSlider) {
            this.refreshSliderAfterFilter();
          }

          this.updateLightboxDataSource();
        }

        // Live fallback when cache is unavailable (e.g. variant not in list).
        computeVisibleIds(variant) {
          const variantValues = this.getVariantOptionValues(variant);
          const isFilteringActive = this.hasActiveOptionFiltering(variantValues);
          const visibleIds = new Set();

          this.querySelectorAll('.product__media-item').forEach((item) => {
            if (this.shouldShowMediaItem(item, variantValues, isFilteringActive)) {
              visibleIds.add(item.dataset.mediaId);
            }
          });

          return visibleIds;
        }

        refreshSliderAfterFilter() {
          const slider = this.sliderInstance?.slider;
          const thumbs = this.thumbsInstance?.slider;

          if (slider) {
            const { activeIndex, slides } = slider;
            slider.update();

            if (slides.length === 0) {
              return;
            }

            const nextIndex = Math.min(activeIndex, slides.length - 1);
            slider.slideTo(nextIndex, 0, false);
            this.syncGalleryHeight(slider);
          }

          if (thumbs) {
            thumbs.update();
          }
        }

        syncGalleryHeight(swiper = this.sliderInstance?.slider) {
          if (!swiper?.params?.autoHeight) {
            return;
          }

          const activeSlide = swiper.slides[swiper.activeIndex];

          if (!activeSlide) {
            return;
          }

          const height = activeSlide.offsetHeight;

          if (height > 0) {
            swiper.wrapperEl.style.height = `${height}px`;
          }
        }

        buildLightboxDataSource() {
          const dataSource = [];
          const allMedia = this.getVisibleMediaItems();

          allMedia.forEach((media) => {
            const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

            let source = {
              id: mediaIndex,
              mediaType: mediaType,
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video': {
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

                source = {
                  ...source,
                  html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`,
                };
                break;
              }
              default:
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight,
                };
                break;
            }

            dataSource.push(source);
          });

          return dataSource;
        }

        updateLightboxDataSource() {
          if (!this.lightbox) {
            return;
          }

          this.lightbox.options.dataSource = this.buildLightboxDataSource();
        }

        slideToMediaId(mediaId) {
          const slider = this.sliderInstance?.slider;

          if (!slider) {
            return false;
          }

          const slideIndex = [...slider.slides].findIndex((slide) => slide.dataset?.mediaId === String(mediaId));

          if (slideIndex >= 0) {
            slider.slideTo(slideIndex, 0, false);
            this.syncGalleryHeight(slider);
            return true;
          }

          return false;
        }

        slideToFirstVisible() {
          const slider = this.sliderInstance?.slider;

          if (!slider || slider.slides.length === 0) {
            return;
          }

          slider.slideTo(0, 0, false);
          this.syncGalleryHeight(slider);
        }

        handleSliderAfterInit() {
          this.sliderInstance.slider.on('afterInit', (swiper) => {
            const { slides, activeIndex } = swiper;

            if (slides[activeIndex]) {
              const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
              this.toggleSliderDraggableState(!isModelMediaType);
            }

            this.syncGalleryHeight(swiper);
          });
        }

        handleSlideChange() {
          this.sliderInstance.slider.on('realIndexChange', (swiper) => {
            const { slides, activeIndex, thumbs } = swiper;

            if (thumbs.swiper) {
              thumbs.swiper.slideTo(activeIndex);
            }

            if (slides[activeIndex]) {
              this.playActiveMedia(slides[activeIndex]);

              const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
              this.toggleSliderDraggableState(!isModelMediaType);
            }

            this.syncGalleryHeight(swiper);
          });

          this.sliderInstance.slider.on('slideChangeTransitionEnd', (swiper) => {
            this.syncGalleryHeight(swiper);
          });
        }

        setActiveMedia(variant, { filter = true } = {}) {
          if (!variant) return;

          if (filter) {
            this.filterMediaByVariant(variant);
          }

          let featuredMedia = variant.featured_media;

          if (!featuredMedia && variant.featured_image) {
            featuredMedia = {
              id: variant.featured_image.id,
              position: variant.featured_image.position || 0,
              media_type: 'image',
            };
          }

          if (!featuredMedia) {
            this.slideToFirstVisible();
            return;
          }

          if (this.sliderInstance?.slider) {
            if (!this.slideToMediaId(featuredMedia.id)) {
              this.slideToFirstVisible();
            }
          } else if (filter) {
            // Only reorder DOM when a full filter/variant-change is in progress.
            // Skipping sortMediaItems when filter=false prevents DOM mutations
            // that would cause a visible jump on initial pre-filtered load.
            this.sortMediaItems(variant, featuredMedia);
          }
        }

        sortMediaItems(variant, featuredMedia) {
          if (!featuredMedia) {
            featuredMedia =
              variant.featured_media ||
              (variant.featured_image
                ? {
                    id: variant.featured_image.id,
                    position: variant.featured_image.position || 0,
                    media_type: 'image',
                  }
                : null);
          }

          if (!featuredMedia) return;

          let newMedias = this.elements.mediaItems.filter((media) => this.isMediaItemInGallery(media));

          newMedias.sort(function (a, b) {
            return a.dataset.mediaIndex - b.dataset.mediaIndex;
          });

          newMedias.some((media, index) => {
            if (media.dataset.mediaId == featuredMedia.id) {
              const [element] = newMedias.splice(index, 1);
              newMedias.unshift(element);
              return true;
            }
          });

          this.elements.mediaList.innerHTML = '';
          newMedias.forEach((media) => {
            this.elements.mediaList.appendChild(media);
          });

          if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
            const selectedMedia = this.querySelector(
              `.product__media-item[data-media-id="${featuredMedia.id}"]`,
            );
            if (selectedMedia) {
              window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
            }
          }
        }
      },
    );
  });
}
